import { RfqTemplateType } from '@lune-fe/dapi-models'
import { Divider } from '@lune-fe/lune-components-lib'
import { Button, Modal, Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { createRfqTemplate, updateRfqTemplate } from 'endpoints/dapi'
import useMappings from 'hooks/useMappings'
import useTitle from 'hooks/useTitle'
import AutoFillLabel from 'views/CalculateEmissions/RFQ/AutoFillLabel'
import { StageContext } from 'views/CalculateEmissions/RFQ/components/MultiStepFlow'
import ProgressSteps, { MappingSteps } from 'views/CalculateEmissions/RFQ/components/ProgressSteps'
import RouteMethodPicker, {
    RouteType,
} from 'views/CalculateEmissions/RFQ/components/RouteMethodPicker'
import { RouteProps } from 'views/CalculateEmissions/RFQ/components/RoutePicker'
import SharedLayout from 'views/CalculateEmissions/RFQ/components/SharedLayout'
import TransportModePicker from 'views/CalculateEmissions/RFQ/components/TransportModePicker'
import SaveTemplateModal from 'views/CalculateEmissions/RFQ/modals/SaveTemplateModal'
import {
    generateUnknownErrorInfo,
    getMappingInfo,
    Info,
    uploadRfqFile,
} from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import {
    RfqTemplateData,
    RouteMapping,
    TemplateLegInfo,
    TransportModeMapping,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    AUTOFILL_BASIC_EMPTY,
    AutoFillEntry,
    AutoFillEntryWithUpdate,
    AutoFillState,
    LegAutoFill,
    LegAutoFillWithUpdate,
    OptionalTransportModeMapping,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'

import { mergeTemplateAndLlmState, showClientAccountsStep } from './utils/utils'

function toSaveLoadData(state: RFQTemplateState): {
    load: RfqTemplateData['load']
    loadSize: RfqTemplateData['loadSize']
} {
    // We have verifications on these two conditions on the components, it should
    // never happen. The checks allows us to narrow the types
    if (state.load === undefined && state.loadSize === undefined) {
        throw new Error('No load information found on state')
    } else if (
        (state.load && state.load.source !== 'column') ||
        (state.loadSize && state.loadSize.source !== 'column')
    ) {
        throw new Error('Load information not coming from column')
    }

    if (state.load === undefined) {
        return {
            loadSize: undefined,
            load: {
                ...state.loadSize!,
                valueMapping: { source: 'unit', unit: 'TEU' },
            },
        }
    } else {
        return {
            load: state.load,
            loadSize: state.loadSize,
        }
    }
}

const TemplateLeg = ({
    sheetType,
    context,
}: {
    sheetType: RfqTemplateType
    context: StageContext
}) => {
    const { title } = getMappingInfo(sheetType)
    useTitle(`Legs - ${title}`)
    const navigate = useNavigate()
    const { enqueueSnackbar: snackbar } = useSnackbar()
    const { refetch } = useMappings(sheetType)
    const [state, setState] = useState<RFQTemplateState | undefined>()
    const [transportModeMapping, setTransportModeMapping] = useState<OptionalTransportModeMapping>()
    const [routeType, setRouteType] = useState<RouteType | undefined>(undefined)
    const [originRouteMapping, setOriginRouteMapping] = useState<RouteMapping | undefined>()
    const [destinationRouteMapping, setDestinationRouteMapping] = useState<
        RouteMapping | undefined
    >()

    const [modal, setModal] = useState<{ subject: string[]; description?: string } | undefined>(
        undefined,
    )

    const [transportError, setTransportError] = useState<string | undefined>()
    const [transportModeMappingError, setTransportModeMappingError] = useState<string | undefined>()
    const [routeTypeError, setRouteTypeError] = useState<string | undefined>(undefined)
    const [originError, setOriginError] = useState<RouteProps['errors']>(undefined)
    const [destinationError, setDestinationError] = useState<RouteProps['errors']>(undefined)
    const [currentLegAutoFill, setCurrentLegAutoFill] = useState<LegAutoFill>(AUTOFILL_BASIC_EMPTY)

    useEffect(() => {
        if (!context.state) {
            snackbar(`File could not be found.`)
            return
        }
        const storedState = context.state
        if (!storedState.sheet) {
            throw new Error(`Programming error. State should be filled and with data`)
        }
        const state = storedState as RFQTemplateState
        setState(state)
    }, [])

    useEffect(() => {
        let currentLeg: TemplateLegInfo | undefined
        let currentLegAutoFill: LegAutoFill | undefined
        if (state && state.legs.length >= state.currentLeg) {
            currentLeg = state.legs[state.currentLeg - 1]
            currentLegAutoFill = state.autoFillState.legs[state.currentLeg - 1]
        }
        setTransportModeMapping(currentLeg?.transportMode)
        setOriginRouteMapping(currentLeg?.origin)
        setDestinationRouteMapping(currentLeg?.destination)
        setRouteType(
            currentLeg?.origin.type
                ? currentLeg.origin.type === 'Distance'
                    ? 'Distance'
                    : 'Origin and Destination'
                : undefined,
        )
        setErrors()

        if (currentLegAutoFill) {
            setCurrentLegAutoFill(currentLegAutoFill)
        }
    }, [state?.currentLeg])

    const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false)
    const [isUploading, setIsUploading] = useState<boolean>(false)

    // eslint-disable-next-line complexity
    const getLocationMappingErrors = (
        modePickerPrefix: string,
        mapping?: RouteMapping,
        routeType?: RouteType,
    ): RouteProps['errors'] => {
        if (!mapping) {
            if (routeType === 'Distance') {
                return { distance: `Distance mapping required` }
            }
            return { modePicker: `${modePickerPrefix} mapping required` }
        }
        if (
            mapping.type === 'Address' &&
            !mapping.countryCode &&
            !mapping.city &&
            !mapping.postcode &&
            !mapping.streetLine
        ) {
            return { modePicker: 'Address needs at least one property' }
        } else if (mapping.type === 'Airport code' && !mapping.airportCode) {
            return { airportCode: 'Airport code mapping is required' }
        } else if (mapping.type === 'Port code' && !mapping.portCode) {
            return { portCode: 'Port code mapping is required' }
        } else if (mapping.type === 'Geo coordinates') {
            const latError = !mapping.lat ? 'Latitude mapping is required' : undefined
            const lonError = !mapping.lon ? 'Longitude mapping is required' : undefined
            if (latError || lonError) {
                return {
                    latitude: latError,
                    longitude: lonError,
                }
            }
        } else if (mapping.type === 'Distance' && !mapping.distance) {
            return { distance: 'Distance mapping is required' }
        } else {
            return undefined
        }
    }

    const getMappingErrors = (): {
        transportMode: string | undefined
        transportModeMapping: string | undefined
        route: string | undefined
        origin: RouteProps['errors']
        destination: RouteProps['errors']
    } => {
        return {
            transportMode: !transportModeMapping ? 'Transport Mode mapping required' : undefined,
            transportModeMapping:
                transportModeMapping && !transportModeMapping.mode
                    ? 'Transport Mode column selection required'
                    : undefined,
            route: routeType === undefined ? 'Mandatory' : undefined,
            origin: getLocationMappingErrors('Origin', originRouteMapping, routeType),
            destination: getLocationMappingErrors(
                'Destination',
                destinationRouteMapping,
                routeType,
            ),
        }
    }

    const setErrors = (errors?: ReturnType<typeof getMappingErrors>): void => {
        setTransportError(errors?.transportMode)
        setTransportModeMappingError(errors?.transportModeMapping)
        setRouteTypeError(errors?.route)
        setOriginError(errors?.origin)
        setDestinationError(errors?.destination)
    }

    const validateLeg = (): boolean => {
        const errors = getMappingErrors()
        setErrors(errors)
        // Any error in the input can be considered as failure to validate the leg
        return !(
            errors.transportMode ||
            errors.transportModeMapping ||
            errors.origin ||
            errors.destination ||
            errors.route
        )
    }

    const openSaveModal = (): void => {
        if (!validateLeg()) {
            return
        }
        setSaveModalOpen(true)
    }

    const getUpdatedLegs = (): { legs: TemplateLegInfo[]; autoFill: AutoFillState } | undefined => {
        if (!state) return undefined
        if (!validateLeg()) {
            return
        }
        const updatedLegs = [...state.legs]
        const legIndex = state.currentLeg - 1
        updatedLegs[legIndex] = {
            // We have checks to validate we can safely cast here
            transportMode: transportModeMapping as TransportModeMapping,
            origin: originRouteMapping!,
            destination: destinationRouteMapping!,
        }
        const autoFill = { ...state.autoFillState, legs: [...state.autoFillState.legs] }
        autoFill.legs[legIndex] = currentLegAutoFill
        return { legs: updatedLegs, autoFill }
    }

    const handleNextLeg = () => {
        if (!state) return
        const updatedLegs = getUpdatedLegs()
        if (!updatedLegs) {
            return
        }

        const newState = {
            ...state,
            currentLeg: state.currentLeg + 1,
            legs: updatedLegs.legs,
            autoFill: updatedLegs.autoFill,
        }
        setState(newState)
        context.updateState(newState)
    }

    const handleOperationError = async (info?: Info) => {
        setIsUploading(false)
        setSaveModalOpen(false)
        setModal(info ?? generateUnknownErrorInfo())
    }

    const handleSave = async (templateName: string) => {
        if (!state) return
        const updatedLegs = getUpdatedLegs()
        if (!updatedLegs) {
            return
        }

        const loadData = toSaveLoadData(state)
        const mappingTemplateData: RfqTemplateData = {
            sheetName: state.sheetName,
            headerRow: state.headerRow,
            load: loadData.load,
            loadSize: loadData.loadSize,
            legs: updatedLegs.legs,
            inferredHeaderRow: state.inferredHeaderRow,
            shippedAt: state.shippedAt,
            shipmentId: state.shipmentId,
            shipmentIdAsIdempotencyKey: state.shipmentIdAsIdempotencyKey,
            clientAccountName: state.clientAccountName,
            clientAccountCurrency: state.clientAccountCurrency,
        }
        const finalTemplate = mergeTemplateAndLlmState(
            mappingTemplateData,
            state.headers,
            state.llmResults,
        )
        const rfqTemplate = await createRfqTemplate(templateName, finalTemplate, sheetType)
        refetch()
        const uploadOperation = await uploadRfqFile(
            state.sheetType === 'rfq' ? { workbook: state.workbook } : { csvFile: state.workbook },
            { mappingId: rfqTemplate.id, jobSettings: state.jobSettings },
        )
        if (uploadOperation.result === 'success') {
            navigate('/imports')
        } else {
            handleOperationError(uploadOperation.info)
        }
    }

    const handleUpdate = async (templateName: string) => {
        if (!state) return
        const updatedLegs = getUpdatedLegs()
        if (!updatedLegs) {
            return
        }
        const loadData = toSaveLoadData(state)
        const mappingTemplateData: RfqTemplateData = {
            sheetName: state.sheetName,
            headerRow: state.headerRow,
            load: loadData.load,
            loadSize: loadData.loadSize,
            legs: updatedLegs.legs,
            shippedAt: state.shippedAt,
            inferredHeaderRow: state.inferredHeaderRow,
            shipmentId: state.shipmentId,
            shipmentIdAsIdempotencyKey: state.shipmentIdAsIdempotencyKey,
            clientAccountName: state.clientAccountName,
            clientAccountCurrency: state.clientAccountCurrency,
        }
        const finalTemplate = mergeTemplateAndLlmState(
            mappingTemplateData,
            state.headers,
            state.llmResults,
        )
        await updateRfqTemplate(state.rfqTemplate!.id, templateName, finalTemplate)
        refetch()
        navigate('/imports')
        setSaveModalOpen(false)
    }

    const handleCancel = () => {
        setSaveModalOpen(false)
    }

    const handlePreviousLeg = () => {
        if (!state) return
        if (state.currentLeg > 1) {
            setState({
                ...state,
                currentLeg: state.currentLeg - 1,
            })
        } else {
            context.navigate('shippingDetails')
        }
    }

    // Used to create our autofill objects while avoiding manual typing of any properties.
    function buildAutoFill<Property extends keyof LegAutoFill>(
        propertyName: Property,
    ): LegAutoFillWithUpdate[Property] {
        // Generates what our components expect to have as auto fill information based on
        // a property/subproperty pair. This means showing the auto fill value alongside
        // a callback that is used to update it.
        function generateSubProperty<SubProperty extends keyof LegAutoFill[Property]>(
            subPropertyName: SubProperty,
            subSubPropertyName: keyof LegAutoFill[Property][SubProperty],
        ): AutoFillEntryWithUpdate {
            const makeUpdater = (keyToUpdate: 'column' | 'mapping') => {
                return (value: boolean) =>
                    setCurrentLegAutoFill((prev) => {
                        return {
                            ...prev,
                            [propertyName]: {
                                ...prev[propertyName],
                                [subPropertyName]: {
                                    ...prev[propertyName][subPropertyName],
                                    [subSubPropertyName]: {
                                        ...prev[propertyName][subPropertyName][subSubPropertyName],
                                        [keyToUpdate]: value,
                                    },
                                },
                            },
                        }
                    })
            }

            const v = currentLegAutoFill[propertyName][subPropertyName][
                subSubPropertyName
            ] as AutoFillEntry
            return {
                column: { value: v.column, update: makeUpdater('column') },
                mapping: { value: v.mapping, update: makeUpdater('mapping') },
            }
        }

        // Typifying things here is an enormous pain and produces lots of boilerplate due to working
        // with `Object.keys` and similar types that perform some erasure. Since we guarantee that
        // `LegAutoFill` and `LegAutoFillWithUpdate` have the same subKeys and subSubKeys (only the
        // leaf values are different), let's rely on `any` here to make things easier for us
        const autoFill: any = {}
        // Gather all subkeys in the provided property so we can go through all of them and
        // generate the auto fill information our components expect. It accesses the state.
        type SubKeyProperty = LegAutoFill[Property]
        const subKeys = Object.keys(currentLegAutoFill[propertyName]) as Array<keyof SubKeyProperty>
        for (const subKey of subKeys) {
            autoFill[subKey] = {}
            type SubSubKeyProperty = SubKeyProperty[keyof SubKeyProperty]
            const subKeyProperty = currentLegAutoFill[propertyName][subKey] as {
                [k in keyof SubSubKeyProperty]: AutoFillEntry
            }
            const subSubKeys = Object.keys(subKeyProperty) as Array<keyof SubSubKeyProperty>
            for (const subSubKey of subSubKeys) {
                autoFill[subKey][subSubKey] = generateSubProperty(subKey, subSubKey)
            }
        }
        return autoFill
    }

    return (
        <>
            <Modal
                fullWidth
                open={modal !== undefined}
                onClose={() => {}}
                header={
                    <Box>
                        {(modal?.subject ?? []).map((e, i) => (
                            <Text
                                key={i}
                                variant={'h5'}
                                sx={{
                                    textAlign: 'center',
                                }}
                            >
                                {e}
                            </Text>
                        ))}
                    </Box>
                }
                subheader={
                    <Text variant={'body1'} sx={{ whiteSpace: 'pre-wrap' }}>
                        {modal?.description}
                    </Text>
                }
                actions={
                    <Button variant={'contained'} onClick={() => setModal(undefined)} wide>
                        OK
                    </Button>
                }
            />
            <SharedLayout
                loading={!state}
                stepsComponent={
                    <>
                        <ProgressSteps
                            step={MappingSteps.LEGS}
                            subLabel={`${state?.currentLeg} of ${state?.numberOfLegs}`}
                            showClientAccounts={showClientAccountsStep(state)}
                        />
                        <Text variant={'h4'}>Leg {state?.currentLeg} details</Text>
                        <Box sx={{ mt: 3 }}>
                            <AutoFillLabel />
                        </Box>
                    </>
                }
                handleNext={
                    state?.currentLeg && state.numberOfLegs
                        ? () => {
                              state.currentLeg < state.numberOfLegs
                                  ? handleNextLeg()
                                  : openSaveModal()
                          }
                        : undefined
                }
                handlePrev={() => {
                    handlePreviousLeg()
                }}
                prevButtonLabel={
                    state?.currentLeg
                        ? state.currentLeg === 1
                            ? 'Shipping details'
                            : `Leg ${state.currentLeg - 1}`
                        : undefined
                }
                nextButtonLabel={
                    state?.currentLeg && state.numberOfLegs
                        ? state.currentLeg < state.numberOfLegs
                            ? `Leg ${state.currentLeg + 1}`
                            : state.rfqTemplate?.name
                              ? 'Mapping name'
                              : 'Calculate emissions'
                        : undefined
                }
            >
                {state && (
                    <>
                        <TransportModePicker
                            error={transportError}
                            modeMappingError={transportModeMappingError}
                            state={state}
                            transportModeMapping={transportModeMapping}
                            onUpdate={(val) => {
                                if (val && !!val.mode) {
                                    setTransportError(undefined)
                                }
                                setTransportModeMappingError(
                                    val && !val.mode
                                        ? 'Transport Mode column selection required'
                                        : undefined,
                                )
                                setTransportModeMapping(val)
                            }}
                            autoFill={buildAutoFill('transportMode')}
                        />
                        <Divider />
                        <RouteMethodPicker
                            state={state}
                            routeType={routeType}
                            originRouteMapping={originRouteMapping}
                            destinationRouteMapping={destinationRouteMapping}
                            routeTypeOnUpdate={(val) => {
                                setRouteType(val)
                                if (val) {
                                    setRouteTypeError(undefined)
                                }
                            }}
                            originOnUpdate={(val, error) => {
                                setOriginRouteMapping(val)
                                setOriginError(error)
                            }}
                            destinationOnUpdate={(val, error) => {
                                setDestinationRouteMapping(val)
                                setDestinationError(error)
                            }}
                            originAutoFill={buildAutoFill('origin')}
                            destinationAutoFill={buildAutoFill('destination')}
                            routeTypeError={routeTypeError}
                            originError={originError}
                            destinationError={destinationError}
                        />
                        <SaveTemplateModal
                            modalOpen={saveModalOpen}
                            defaultValue={state.rfqTemplate?.name}
                            isUploading={isUploading}
                            onModalClose={(res) => {
                                if (res) {
                                    setIsUploading(true)
                                    const methodToCall =
                                        state.rfqTemplate !== undefined ? handleUpdate : handleSave
                                    methodToCall(res).catch((_e) => handleOperationError())
                                } else {
                                    handleCancel()
                                }
                            }}
                        />
                    </>
                )}
            </SharedLayout>
        </>
    )
}

export default TemplateLeg
